import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ClassLessonAlarmOffset } from '../../utils/classLessonAlarmSettings';
import { classLessonAlarmOffsetLabel } from '../../utils/classLessonAlarmSettings';

export interface ClassLessonAlarmPopupInfo {
  time: string;
  offset: ClassLessonAlarmOffset;
  name?: string;
  /** 처음 울린 구간(분). 이후 2분전·1분전 음성만 나와도 게이지는 이 기준으로 유지 */
  phaseMinutes?: number;
  channelLabel?: string;
  /** 교시 시작(게이지 0) 시각 ms — 있으면 남은 시간을 실제 시계로 계산(늦게 열려도 흐른 만큼 깎인 상태) */
  startsAtMs?: number;
}

function parseHHMMToMinutes(hhmm: string): number | null {
  const m = /^(\d{1,2}):([0-5]\d)$/.exec((hhmm ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || h < 0 || h > 23) return null;
  return h * 60 + min;
}

/** 교시 시작 시각이 지났는지(최소화 세션 정리용) */
export function isClassLessonAlarmTimePassed(alarmTime: string, now: Date = new Date()): boolean {
  const alarmMins = parseHHMMToMinutes(alarmTime);
  if (alarmMins == null) return true;
  return now.getHours() * 60 + now.getMinutes() >= alarmMins;
}

const ClassLessonAlarmPopup: React.FC<{
  open: boolean;
  info: ClassLessonAlarmPopupInfo | null;
  onStopAlarm: () => void;
  onClosePopup: () => void;
}> = ({ open, info, onStopAlarm, onClosePopup }) => {
  const typeMaxMinutes = info?.offset === '1M' ? 1 : info?.offset === '5M' ? 5 : 3;

  const timerContainerRef = useRef<HTMLDivElement>(null);
  const popupContentRef = useRef<HTMLDivElement>(null);
  const [circleSize, setCircleSize] = useState(350);
  const [modalWidth, setModalWidth] = useState(0);
  const [timerTextSize, setTimerTextSize] = useState(5.5);
  const timerIntervalRef = useRef<number | null>(null);
  /** 시각+오프셋 세션 (phase 제외 — 분 단위 음성마다 게이지 리셋 방지) */
  const sessionKeyRef = useRef('');
  const sessionTotalSecondsRef = useRef(0);
  const startsAtMsRef = useRef<number | undefined>(undefined);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [sessionTotalSeconds, setSessionTotalSeconds] = useState(0);

  const [isDarkMode, setIsDarkMode] = useState(
    () => document.documentElement.getAttribute('data-theme') !== 'light'
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.getAttribute('data-theme') !== 'light');
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => observer.disconnect();
  }, []);

  // 새 알람 세션(시각·오프셋)일 때만 게이지 초기화. phaseMinutes 변경은 무시.
  useEffect(() => {
    if (!info) {
      sessionKeyRef.current = '';
      sessionTotalSecondsRef.current = 0;
      setRemainingSeconds(0);
      setSessionTotalSeconds(0);
      if (timerIntervalRef.current != null) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      return;
    }

    const sessionKey = `${info.offset}|${info.time}`;
    startsAtMsRef.current = info.startsAtMs;
    const remainingByClock = () =>
      Math.max(
        0,
        Math.min(
          sessionTotalSecondsRef.current,
          Math.ceil(((startsAtMsRef.current ?? 0) - Date.now()) / 1000)
        )
      );
    if (sessionKeyRef.current === sessionKey) {
      // 같은 세션에서 기준 시각이 바뀐 경우(늦게 잡힌 1분 전 구간 → 지금부터 1분) 게이지 즉시 반영
      if (info.startsAtMs != null) setRemainingSeconds(remainingByClock());
      return;
    }

    const initialMinutes =
      info.phaseMinutes != null && info.phaseMinutes >= 1
        ? Math.min(info.phaseMinutes, typeMaxMinutes)
        : typeMaxMinutes;
    const newTotal = initialMinutes * 60;
    sessionKeyRef.current = sessionKey;
    sessionTotalSecondsRef.current = newTotal;
    setSessionTotalSeconds(newTotal);
    setRemainingSeconds(info.startsAtMs != null ? remainingByClock() : newTotal);

    if (timerIntervalRef.current != null) {
      window.clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    if (newTotal > 0 && info.startsAtMs != null) {
      timerIntervalRef.current = window.setInterval(() => {
        setRemainingSeconds(remainingByClock());
      }, 1000);
    } else if (newTotal > 0) {
      timerIntervalRef.current = window.setInterval(() => {
        setRemainingSeconds((prev) => {
          if (prev <= 1) {
            if (timerIntervalRef.current != null) {
              window.clearInterval(timerIntervalRef.current);
              timerIntervalRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  }, [info, typeMaxMinutes]);

  // 마운트 동안 타이머 유지(팝업 닫아도 카운트다운 계속 — 에듀코디와 동일)
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current != null) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, []);

  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(Math.max(0, seconds) / 60);
    const secs = Math.max(0, seconds) % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const titleFontSize = useMemo(() => {
    const baseSize = 3.5;
    const minSize = 1.4;
    if (!modalWidth || modalWidth >= 400) return `${baseSize}rem`;
    return `${Math.max(minSize, baseSize * (modalWidth / 400))}rem`;
  }, [modalWidth]);

  const labelFontSize = useMemo(() => {
    const baseSize = 3;
    const minSize = 1.2;
    if (!modalWidth || modalWidth >= 400) return `${baseSize}rem`;
    return `${Math.max(minSize, baseSize * (modalWidth / 400))}rem`;
  }, [modalWidth]);

  const typeFontSize = useMemo(() => {
    const baseSize = 1.2;
    const minSize = 0.75;
    if (!modalWidth || modalWidth >= 400) return `${baseSize}rem`;
    return `${Math.max(minSize, baseSize * (modalWidth / 400))}rem`;
  }, [modalWidth]);

  const remainingSecondsRef = useRef(remainingSeconds);
  remainingSecondsRef.current = remainingSeconds;
  const circleSizeRef = useRef(circleSize);
  circleSizeRef.current = circleSize;
  const modalWidthRef = useRef(modalWidth);
  modalWidthRef.current = modalWidth;

  const applyTimerTextSize = useCallback(
    (width: number, size: number, seconds: number) => {
      const stroke = Math.max(16, size * 0.06);
      const innerDiameter = Math.max(48, size - stroke * 2 - 20);
      const maxWidthForText = innerDiameter * 0.7;
      const maxHeightForText = innerDiameter * 0.4;

      const tempSpan = document.createElement('span');
      tempSpan.style.fontFamily = "Rajdhani, Orbitron, DS-Digital, LCD, 'Courier New', monospace";
      tempSpan.style.fontWeight = 'bold';
      tempSpan.style.fontSize = '5.5rem';
      tempSpan.style.fontVariantNumeric = 'tabular-nums';
      tempSpan.style.visibility = 'hidden';
      tempSpan.style.position = 'absolute';
      tempSpan.style.whiteSpace = 'nowrap';
      tempSpan.style.top = '-9999px';
      tempSpan.textContent = formatTime(seconds);
      document.body.appendChild(tempSpan);
      const textWidthAtMax = tempSpan.offsetWidth;
      const textHeightAtMax = tempSpan.offsetHeight;
      document.body.removeChild(tempSpan);

      let sizeFromWidth = 5.5;
      if (textWidthAtMax > 0 && textWidthAtMax > maxWidthForText) {
        sizeFromWidth = (maxWidthForText / textWidthAtMax) * 5.5;
      }
      let sizeFromHeight = 5.5;
      if (textHeightAtMax > 0 && textHeightAtMax > maxHeightForText) {
        sizeFromHeight = (maxHeightForText / textHeightAtMax) * 5.5;
      }
      if (width > 0) {
        const modalCap = width * 0.5;
        if (textWidthAtMax > 0 && textWidthAtMax > modalCap) {
          sizeFromWidth = Math.min(sizeFromWidth, (modalCap / textWidthAtMax) * 5.5);
        }
      }
      const next = Math.max(1.05, Math.min(sizeFromWidth, sizeFromHeight, 5.5));
      setTimerTextSize((prev) => (Math.abs(prev - next) < 0.01 ? prev : next));
    },
    [formatTime]
  );

  useEffect(() => {
    if (!info || !open) return undefined;
    const popup = popupContentRef.current;
    const container = timerContainerRef.current;
    if (!popup || !container) return undefined;

    let rafId = 0;
    const updateSizes = () => {
      rafId = 0;
      const popupEl = popupContentRef.current;
      const containerEl = timerContainerRef.current;
      if (!popupEl || !containerEl) return;

      const popupRect = popupEl.getBoundingClientRect();
      const currentModalWidth = popupRect.width;
      setModalWidth((prev) => (Math.abs(prev - currentModalWidth) < 1 ? prev : currentModalWidth));

      // 타이머 컨테이너는 circleSize에 따라 다시 변하므로 관찰하지 않고,
      // 팝업 폭 기준으로만 원 크기를 잡아 ResizeObserver 루프를 피한다.
      const availableSize = Math.min(currentModalWidth * 0.72, containerEl.clientHeight || currentModalWidth) * 0.98;
      const size = Math.max(120, Math.min(availableSize || 350, 600));
      setCircleSize((prev) => (Math.abs(prev - size) < 1 ? prev : size));
      applyTimerTextSize(currentModalWidth, size, remainingSecondsRef.current);
    };

    const scheduleUpdate = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(updateSizes);
    };

    const initTimer = window.setTimeout(scheduleUpdate, 0);
    const resizeObserver = new ResizeObserver(scheduleUpdate);
    // 크기 변경의 원인이 되는 타이머 영역은 observe하지 않는다.
    resizeObserver.observe(popup);
    window.addEventListener('resize', scheduleUpdate);
    return () => {
      window.clearTimeout(initTimer);
      if (rafId) window.cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [open, info, applyTimerTextSize]);

  useEffect(() => {
    if (!open || !info) return;
    applyTimerTextSize(modalWidthRef.current, circleSizeRef.current, remainingSeconds);
  }, [open, info, remainingSeconds, applyTimerTextSize]);

  const totalSeconds = sessionTotalSeconds > 0 ? sessionTotalSeconds : sessionTotalSecondsRef.current;
  const progress = totalSeconds > 0 ? remainingSeconds / totalSeconds : 0;
  const strokeWidth = Math.max(16, circleSize * 0.06);
  const radius = Math.min(circleSize / 2 - strokeWidth / 2 - 4, circleSize * 0.48);
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - progress);
  const center = circleSize / 2;
  const typeText = info ? `-${classLessonAlarmOffsetLabel(info.offset)} 알람` : '';

  if (!open || !info) return null;

  return createPortal(
    <div
      className="cp-class-lesson-alarm-popup-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="수업 알람"
      data-tutorial-alarm="popup"
      onClick={onClosePopup}
    >
      <div
        className="cp-class-lesson-alarm-popup-content"
        ref={popupContentRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-class-lesson-alarm-popup-title-row">
          <span className="cp-class-lesson-alarm-popup-time" style={{ fontSize: titleFontSize }}>
            {info.time || '00:00'}
          </span>
          <span className="cp-class-lesson-alarm-popup-label" style={{ fontSize: labelFontSize }}>
            알람
          </span>
        </div>
        <div className="cp-class-lesson-alarm-popup-type" style={{ fontSize: typeFontSize }}>
          {typeText}
          {info.channelLabel ? ` · ${info.channelLabel}` : ''}
          {info.name ? ` · ${info.name}` : ''}
        </div>
        <div className="cp-class-lesson-alarm-popup-timer" ref={timerContainerRef}>
          <div className="cp-class-lesson-alarm-popup-timer-circle">
            <svg
              width={circleSize}
              height={circleSize}
              viewBox={`0 0 ${circleSize} ${circleSize}`}
              style={{ overflow: 'visible' }}
            >
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={isDarkMode ? '#333333' : 'var(--cp-border, #c8cdd4)'}
                strokeWidth={strokeWidth}
              />
              <circle
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={isDarkMode ? '#4cc7ae' : 'var(--cp-accent, #3d7ea6)'}
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                transform={`rotate(-90 ${center} ${center})`}
                style={{ transition: 'stroke-dashoffset 0.3s ease' }}
              />
            </svg>
            <div
              className="cp-class-lesson-alarm-popup-timer-text"
              style={{ fontSize: `${timerTextSize}rem` }}
            >
              {formatTime(remainingSeconds)}
            </div>
          </div>
        </div>
        <div className="cp-class-lesson-alarm-popup-actions">
          <button
            type="button"
            className="cp-btn-primary"
            data-tutorial-alarm="popup-stop"
            onClick={onStopAlarm}
          >
            알람끄기
          </button>
          <button
            type="button"
            className="cp-btn-secondary"
            data-tutorial-alarm="popup-close"
            onClick={onClosePopup}
          >
            팝업닫기
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ClassLessonAlarmPopup;
